import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import {
    SovosBox,
    SovosButton,
    SovosDialog,
    SovosDialogActions,
    SovosDialogContent,
    SovosDialogTitle,
    SovosIconButton,
    SovosMenuItem,
    SovosSelect,
    SovosSnackbar,
    SovosSwitch,
    SovosTooltip,
    SovosTypography,
} from "mosaic-react";
import { Add, Close, Delete, Edit, PlayArrow } from "mosaic-react-icons";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import TranslationModal from "./TranslationModal";
import { formatConditionSummary } from "./featureFlags";

const STATE_LABELS = {
    Active: { label: "Translation status active", dot: "success.main", bg: "success.light", text: "success.dark" },
    Inactive: { label: "Translation status inactive", dot: "text.disabled", bg: "grey.100", text: "text.secondary" },
    Processing: {
        label: "Translation status processing",
        dot: "primary.main",
        bg: "primary.light",
        text: "primary.dark",
    },
};

const STATUS_FILTERS = [
    { value: "All", label: "All translation statuses" },
    { value: "Active", label: "Translation status active" },
    { value: "Inactive", label: "Translation status inactive" },
    { value: "Processing", label: "Translation status processing" },
];
const APPLY_POLL_INTERVAL_MS = 2000;
const APPLY_POLL_MAX_ATTEMPTS = 45;

export const hasTranslationRunCompleted = (translation, previousRunAt) =>
    translation?.state !== "Processing" &&
    Boolean(translation?.last_run_at) &&
    translation.last_run_at !== previousRunAt;

export const getApplyTooltip = ({ canMutate, locallyApplying, state, nonOwnerTooltip }) => {
    if (!canMutate) return nonOwnerTooltip;
    if (locallyApplying) return "This translation is waiting for execution.";
    if (state === "Inactive") return "Activate this translation before applying it.";
    if (state === "Processing") return "This translation is already processing.";
    return "Apply now";
};

const identityTranslate = (strings, ...values) =>
    typeof strings === "string"
        ? strings
        : strings.reduce(
            (message, part, index) => message + part + (values[index] ?? ""),
            "",
        );

export const buildCompletionNotification = (translation, translate = identityTranslate) => {
    const changedRows = translation?.last_run_changed_rows ?? 0;
    if (translation?.last_run_outcome !== "SUCCESS") {
        return {
            type: "error",
            message:
                changedRows > 0
                    ? translate`"${translation.name}" did not complete successfully after updating ${changedRows} row(s). Review the staging data before retrying.`
                    : translate`"${translation.name}" did not complete successfully. No staging changes were applied.`,
        };
    }
    return {
        type: "confirmation",
        message: translate`"${translation.name}" finished — ${changedRows} row(s) updated.`,
    };
};

const formatTimestamp = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleString();
    } catch {
        return iso;
    }
};

const StateBadge = ({ state, i18n }) => {
    const cfg = STATE_LABELS[state] || STATE_LABELS.Inactive;
    return (
        <SovosBox
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 0.75,
                py: 0.25,
                borderRadius: 2,
                bgcolor: cfg.bg,
            }}
        >
            <SovosBox sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: cfg.dot }} />
            <SovosTypography variant="caption" sx={{ color: cfg.text, fontWeight: 600 }}>
                {i18n(cfg.label)}
            </SovosTypography>
        </SovosBox>
    );
};

const TranslationsList = ({
    profileId,
    projectId,
    columnOptions = [],
    canMutate = true,
    nonOwnerTooltip: providedNonOwnerTooltip,
    onList,
    onCreate,
    onUpdate,
    onToggleState,
    onSoftDelete,
    onApply,
    onApplyCompleted,
}) => {
    const { i18n } = useGlobalValue();
    const nonOwnerTooltip =
        providedNonOwnerTooltip || i18n`Only the project owner can manage translations.`;
    const [translations, setTranslations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("All");
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [notification, setNotification] = useState(null);
    const [applyingIds, setApplyingIds] = useState(new Set());
    const previousStatesRef = React.useRef(new Map());
    const applyOperationsRef = React.useRef(new Map());
    const isMountedRef = React.useRef(true);

    const refresh = useCallback(
        async (showLoading = true, notifyCompletion = true) => {
            if (!onList || !profileId || !projectId) return;
            if (showLoading) setLoading(true);
            try {
                const result = await onList(profileId, projectId);
                if (!Array.isArray(result)) {
                    if (isMountedRef.current) {
                        setNotification({
                            type: "error",
                            message: i18n`Translations could not be loaded. Existing results were preserved.`,
                        });
                    }
                    return null;
                }
                const next = result;
                if (!isMountedRef.current) return next;
                const prev = previousStatesRef.current;
                const transitions = [];
                for (const t of next) {
                    const prevState = prev.get(t.id);
                    if (prevState && prevState !== t.state) {
                        if (t.state === "Processing") {
                            transitions.push({ type: "processing", translation: t });
                        } else if (prevState === "Processing" && t.state === "Active") {
                            transitions.push({ type: "active", translation: t });
                        }
                    }
                    prev.set(t.id, t.state);
                }
                const completed = transitions.filter((transition) => transition.type === "active");
                const processing = transitions.filter(
                    (transition) => transition.type === "processing",
                );
                const failedCompletions = completed.filter(
                    (transition) => transition.translation.last_run_outcome !== "SUCCESS",
                );
                if (failedCompletions.length > 0) {
                    setNotification({
                        type: "error",
                        message:
                            failedCompletions.length === 1
                                ? buildCompletionNotification(
                                    failedCompletions[0].translation,
                                    i18n,
                                )
                                      .message
                                : i18n`${failedCompletions.length} translations did not complete successfully.`,
                    });
                } else if (completed.length === 1) {
                    setNotification(buildCompletionNotification(completed[0].translation, i18n));
                } else if (completed.length > 1) {
                    setNotification({
                        type: "confirmation",
                        message: i18n`${completed.length} translations finished.`,
                    });
                } else if (processing.length > 0) {
                    setNotification({
                        type: "info",
                        message:
                            processing.length === 1
                                ? i18n`"${processing[0].translation.name}" is processing…`
                                : i18n`${processing.length} translations are processing…`,
                    });
                }
                if (notifyCompletion && onApplyCompleted) {
                    completed.forEach((transition) => onApplyCompleted(transition.translation));
                }
                setTranslations(next);
                return next;
            } finally {
                if (showLoading && isMountedRef.current) setLoading(false);
            }
        },
        [onApplyCompleted, onList, profileId, projectId],
    );

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        const applyOperations = applyOperationsRef.current;
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            applyOperations.clear();
        };
    }, []);

    useEffect(() => {
        const hasProcessing = translations.some((t) => t.state === "Processing");
        if (!hasProcessing || applyingIds.size > 0) return undefined;
        const handle = setInterval(refresh, 6000);
        return () => clearInterval(handle);
    }, [applyingIds, translations, refresh]);

    const filtered = useMemo(() => {
        if (statusFilter === "All") return translations;
        return translations.filter((t) => t.state === statusFilter);
    }, [translations, statusFilter]);

    const handleOpenCreate = () => {
        setEditing(null);
        setSubmitError(null);
        setModalOpen(true);
    };

    const handleOpenEdit = (translation) => {
        setEditing(translation);
        setSubmitError(null);
        setModalOpen(true);
    };

    const handleSubmit = async (values) => {
        if (!canMutate) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            if (editing) {
                const result = await onUpdate(editing.id, values);
                if (!result) {
                    setSubmitError(i18n`Could not save the translation. Please try again.`);
                    return;
                }
            } else {
                const result = await onCreate({
                    projectId,
                    importProfileId: profileId,
                    ...values,
                });
                if (!result) {
                    setSubmitError(i18n`Could not create the translation. Please try again.`);
                    return;
                }
            }
            setModalOpen(false);
            await refresh();
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggle = async (translation) => {
        if (!canMutate || !onToggleState) return;
        const next = translation.state === "Active" ? "Inactive" : "Active";
        await onToggleState(translation.id, next);
        await refresh();
    };

    const handleApply = async (translation) => {
        if (
            !canMutate ||
            !onApply ||
            translation.state !== "Active" ||
            applyOperationsRef.current.has(translation.id)
        )
            return;

        const operation = Symbol(String(translation.id));
        applyOperationsRef.current.set(translation.id, operation);
        setApplyingIds(new Set(applyOperationsRef.current.keys()));

        try {
            const currentTranslations = await onList(profileId, projectId);
            if (
                !isMountedRef.current ||
                applyOperationsRef.current.get(translation.id) !== operation
            )
                return;
            const current = currentTranslations?.find((item) => item.id === translation.id);
            if (!current || current.state !== "Active") {
                setNotification({
                    type: "error",
                    message: i18n`"${translation.name}" is no longer ready to apply. Refresh and try again.`,
                });
                return;
            }

            const result = await onApply(current.id, profileId, projectId);
            if (
                !isMountedRef.current ||
                applyOperationsRef.current.get(translation.id) !== operation
            )
                return;
            if (result?.queued !== true) {
                setNotification({
                    type: "error",
                    message: i18n`Could not queue "${translation.name}".`,
                });
                await refresh();
                return;
            }

            setNotification({
                type: "info",
                message: i18n`Apply queued for "${translation.name}". Waiting for execution…`,
            });
            const previousRunAt = current.last_run_at;

            for (
                let attempt = 0;
                attempt < APPLY_POLL_MAX_ATTEMPTS &&
                isMountedRef.current &&
                applyOperationsRef.current.get(translation.id) === operation;
                attempt += 1
            ) {
                await new Promise((resolve) => setTimeout(resolve, APPLY_POLL_INTERVAL_MS));
                if (
                    !isMountedRef.current ||
                    applyOperationsRef.current.get(translation.id) !== operation
                )
                    return;
                const next = await refresh(false, false);
                if (
                    !isMountedRef.current ||
                    applyOperationsRef.current.get(translation.id) !== operation
                )
                    return;
                const updated = next?.find((item) => item.id === translation.id);
                if (!updated) continue;

                if (hasTranslationRunCompleted(updated, previousRunAt)) {
                    setNotification(buildCompletionNotification(updated, i18n));
                    if (onApplyCompleted) onApplyCompleted(updated);
                    return;
                }
            }

            if (
                isMountedRef.current &&
                applyOperationsRef.current.get(translation.id) === operation
            ) {
                setNotification({
                    type: "error",
                    message: i18n`"${translation.name}" is still running or timed out. Refresh and retry if needed.`,
                });
            }
        } finally {
            if (applyOperationsRef.current.get(translation.id) === operation) {
                applyOperationsRef.current.delete(translation.id);
                if (isMountedRef.current) {
                    setApplyingIds(new Set(applyOperationsRef.current.keys()));
                }
            }
        }
    };

    const handleDeleteConfirm = async () => {
        if (!canMutate || !onSoftDelete || !pendingDelete) return;
        const target = pendingDelete;
        setPendingDelete(null);
        await onSoftDelete(target.id);
        await refresh();
    };

    return (
        <SovosBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SovosBox
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>
                        {i18n`Status`}
                    </SovosTypography>
                    <SovosSelect
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        size="small"
                        sx={{ minWidth: 120 }}
                    >
                        {STATUS_FILTERS.map(({ value, label }) => (
                            <SovosMenuItem key={value} value={value}>
                                {i18n(label)}
                            </SovosMenuItem>
                        ))}
                    </SovosSelect>
                </SovosBox>
                <SovosTooltip title={canMutate ? "" : nonOwnerTooltip} placement="top">
                    <span>
                        <SovosButton
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleOpenCreate}
                            disabled={!canMutate}
                        >
                            {i18n`New Translation`}
                        </SovosButton>
                    </span>
                </SovosTooltip>
            </SovosBox>

            {loading && (
                <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={14} />
                    <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>
                        {i18n`Loading translations…`}
                    </SovosTypography>
                </SovosBox>
            )}

            {!loading && filtered.length === 0 && (
                <SovosBox
                    sx={{
                        p: 3,
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 1,
                        textAlign: "center",
                    }}
                >
                    <SovosTypography variant="body2" sx={{ color: "text.secondary" }}>
                        {translations.length === 0
                            ? i18n`No translations yet. Use New Translation to create your first rule.`
                            : `${i18n`No translations match`} "${i18n(
                                STATUS_FILTERS.find(({ value }) => value === statusFilter)?.label ||
                                    "All translation statuses",
                            )}".`}
                    </SovosTypography>
                </SovosBox>
            )}

            {!loading && filtered.length > 0 && (
                <SovosBox
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                    }}
                >
                    <SovosBox
                        sx={{
                            display: "grid",
                            gridTemplateColumns:
                                "minmax(180px, 1.4fr) minmax(220px, 2fr) 180px 140px 120px 160px",
                            gap: 1,
                            px: 2,
                            py: 1,
                            bgcolor: "grey.50",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <SovosTypography variant="caption" sx={{ fontWeight: 700 }}>
                            {i18n`Translation name`}
                        </SovosTypography>
                        <SovosTypography variant="caption" sx={{ fontWeight: 700 }}>
                            {i18n`Condition`}
                        </SovosTypography>
                        <SovosTypography variant="caption" sx={{ fontWeight: 700 }}>
                            {i18n`Last run`}
                        </SovosTypography>
                        <SovosTypography variant="caption" sx={{ fontWeight: 700 }}>
                            {i18n`Fixes applied`}
                        </SovosTypography>
                        <SovosTypography variant="caption" sx={{ fontWeight: 700 }}>
                            {i18n`State`}
                        </SovosTypography>
                        <SovosTypography
                            variant="caption"
                            sx={{ fontWeight: 700, textAlign: "right" }}
                        >
                            {i18n`Actions`}
                        </SovosTypography>
                    </SovosBox>
                    {filtered.map((t) => {
                        const sameColumn = translations.filter(
                            (other) =>
                                other.id !== t.id &&
                                other.column_name === t.column_name &&
                                other.state === "Active",
                        );
                        const stateLabel = STATE_LABELS[t.state] || STATE_LABELS.Inactive;
                        const locallyApplying = applyingIds.has(t.id);
                        const canMutateRow =
                            canMutate && t.state !== "Processing" && !locallyApplying;
                        const canApplyNow = canMutate && t.state === "Active" && !locallyApplying;
                        const rawApplyTooltip = getApplyTooltip({
                            canMutate,
                            locallyApplying,
                            state: t.state,
                            nonOwnerTooltip,
                        });
                        const applyTooltip = canMutate ? i18n(rawApplyTooltip) : rawApplyTooltip;
                        return (
                            <SovosBox
                                key={t.id}
                                data-testid={`translation-row-${t.id}`}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "minmax(180px, 1.4fr) minmax(220px, 2fr) 180px 140px 120px 160px",
                                    gap: 1,
                                    px: 2,
                                    py: 1.25,
                                    alignItems: "center",
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                    "&:last-of-type": { borderBottom: 0 },
                                }}
                            >
                                <SovosBox>
                                    <SovosTypography variant="body2" sx={{ fontWeight: 600 }}>
                                        {t.name}
                                    </SovosTypography>
                                    <SovosTypography
                                        variant="caption"
                                        sx={{ color: "text.secondary" }}
                                    >
                                        {t.source === "ai_suggested" ? i18n`AI suggested` : i18n`Manual`}
                                    </SovosTypography>
                                </SovosBox>
                                <SovosBox>
                                    <SovosTypography variant="body2">
                                        {formatConditionSummary(t, i18n)}
                                    </SovosTypography>
                                    {sameColumn.length > 0 && (
                                        <SovosTooltip
                                            title={i18n`Multiple translations target this column. Order-sensitive chains may produce unexpected results.`}
                                            placement="top"
                                        >
                                            <SovosTypography
                                                variant="caption"
                                                sx={{ color: "warning.dark" }}
                                            >
                                                {i18n`Shares column with other rules`}:{" "}
                                                {sameColumn.length}
                                            </SovosTypography>
                                        </SovosTooltip>
                                    )}
                                </SovosBox>
                                <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>
                                    {formatTimestamp(t.last_run_at)}
                                </SovosTypography>
                                <SovosTypography variant="caption">
                                    {t.last_run_changed_rows == null
                                        ? "—"
                                        : t.last_run_changed_rows}
                                </SovosTypography>
                                <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {t.state === "Processing" ? (
                                        <SovosBox
                                            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                                        >
                                            <CircularProgress size={12} />
                                            <StateBadge state={t.state} i18n={i18n} />
                                        </SovosBox>
                                    ) : (
                                        <SovosTooltip
                                            title={canMutate ? "" : nonOwnerTooltip}
                                            placement="top"
                                        >
                                            <span>
                                                <SovosSwitch
                                                    checked={t.state === "Active"}
                                                    onChange={() => handleToggle(t)}
                                                    disabled={!canMutateRow}
                                                    aria-label={`Toggle ${t.name}`}
                                                />
                                            </span>
                                        </SovosTooltip>
                                    )}
                                    <SovosTypography
                                        variant="caption"
                                        sx={{ color: stateLabel.text }}
                                    >
                                        {i18n(stateLabel.label)}
                                    </SovosTypography>
                                </SovosBox>
                                <SovosBox
                                    sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}
                                >
                                    <SovosTooltip title={applyTooltip} placement="top">
                                        <span>
                                            <SovosIconButton
                                                onClick={() => handleApply(t)}
                                                disabled={!canApplyNow}
                                                tooltipText={applyTooltip}
                                                size="small"
                                                aria-label={`Apply ${t.name}`}
                                                data-testid={`translation-${t.id}-apply`}
                                            >
                                                <PlayArrow fontSize="small" />
                                            </SovosIconButton>
                                        </span>
                                    </SovosTooltip>
                                    <SovosTooltip
                                        title={canMutate ? i18n`Edit` : nonOwnerTooltip}
                                        placement="top"
                                    >
                                        <span>
                                            <SovosIconButton
                                                onClick={() => handleOpenEdit(t)}
                                                disabled={!canMutateRow}
                                                tooltipText={i18n`Edit`}
                                                size="small"
                                                aria-label={`Edit ${t.name}`}
                                                data-testid={`translation-${t.id}-edit`}
                                            >
                                                <Edit fontSize="small" />
                                            </SovosIconButton>
                                        </span>
                                    </SovosTooltip>
                                    <SovosTooltip
                                        title={canMutate ? i18n`Delete` : nonOwnerTooltip}
                                        placement="top"
                                    >
                                        <span>
                                            <SovosIconButton
                                                onClick={() => setPendingDelete(t)}
                                                disabled={!canMutateRow}
                                                tooltipText={i18n`Delete`}
                                                size="small"
                                                aria-label={`Delete ${t.name}`}
                                                data-testid={`translation-${t.id}-delete`}
                                            >
                                                <Delete fontSize="small" />
                                            </SovosIconButton>
                                        </span>
                                    </SovosTooltip>
                                </SovosBox>
                            </SovosBox>
                        );
                    })}
                </SovosBox>
            )}

            <TranslationModal
                open={modalOpen}
                mode={editing ? "edit" : "create"}
                initialValues={editing}
                columnOptions={columnOptions}
                onCancel={() => setModalOpen(false)}
                onSubmit={handleSubmit}
                submitting={submitting}
                submitError={submitError}
            />

            <SovosDialog
                open={!!pendingDelete}
                onClose={() => setPendingDelete(null)}
                maxWidth="xs"
                fullWidth
            >
                <SovosDialogTitle>
                    {i18n`Delete translation`}
                    <SovosIconButton
                        onClick={() => setPendingDelete(null)}
                        tooltipText={i18n`Close`}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <Close />
                    </SovosIconButton>
                </SovosDialogTitle>
                <SovosDialogContent>
                    <SovosTypography variant="body2" sx={{ mb: 1 }}>
                        {i18n`Are you sure you want to delete this translation?`}{" "}
                        "{pendingDelete?.name}"
                    </SovosTypography>
                    <SovosTypography
                        variant="caption"
                        sx={{ color: "warning.dark", fontWeight: 600 }}
                    >
                        {i18n`This does not revert data already transformed.`}
                    </SovosTypography>
                </SovosDialogContent>
                <SovosDialogActions>
                    <SovosButton onClick={() => setPendingDelete(null)} variant="text">
                        {i18n`Cancel`}
                    </SovosButton>
                    <SovosButton onClick={handleDeleteConfirm} variant="contained" color="error">
                        {i18n`Delete`}
                    </SovosButton>
                </SovosDialogActions>
            </SovosDialog>

            <SovosSnackbar
                open={!!notification}
                message={notification?.message}
                type={notification?.type}
                onClose={() => setNotification(null)}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </SovosBox>
    );
};

export default TranslationsList;
